import { ServerResponse } from 'http';
import queryString from 'querystring';
import { NextPageContext } from 'next';
import Router, { useRouter } from 'next/router';
import urlParse from 'url-parse';
import UrlPattern from 'url-pattern';
import { getConfig } from '@belong/configs/next/config';
import { isBrowser } from '@belong/env';
import { logger } from '@belong/logging/logger';
import { IOptionalStringMap, IStringMap, removeObjectEmptyProps } from '@belong/type-utils';
import { IRouteDefinition, IRouteMatch } from '@belong/types';
import { MYACCOUNT_INTERNAL_ROUTES } from './routes';

export const isBlockedPage = (path: string, pages: Record<string, boolean>): boolean => {
  return path in pages && pages[path];
};

export const isPDF = (href = ''): boolean => /\.pdf$/i.test(urlParse(href).pathname);

// Next Router does not allow us to pushRoute to external links
// For external links, we need to use window.location.
// Function to check if url's are external links or different domain links.
export const isExternalUrl = (url = ''): boolean => {
  // These links trigger custom actions when the web page is loaded
  // inside a webview on our native app.
  // eg: belong://navigate/notifications/218211 will make the app show the notification details for notification #218211
  if (url.startsWith('belong://')) {
    return true;
  }

  return /^(https?:)?\/{2}/.test(url);
};

export const isRelativeUrl = (url = ''): boolean => /^(\.?\/(?!\/)|#)/.test(url);
export const isAnchor = (url = ''): boolean => url.startsWith('#');
export const isErrorPage = (url = ''): boolean => /^(\/[^/]+)?\/(404|500|_error)$/.test(url);
export const is404Page = (url = ''): boolean => url.endsWith('404');

export enum SERVICE_URLS {
  ACTIVE_SIM = '/new/services',
  ORDER_SIM = '/new/order-sim',
  PICK_A_SIM = '/new/pick-a-sim/:simCategory'
}

export const addParamsToUrl = (url: string, query: IOptionalStringMap): string => {
  const urlParams = url.includes('?') ? url.split('?').pop() : null;
  const params = removeObjectEmptyProps({
    ...queryString.parse(urlParams ?? ''),
    ...query
  });

  return `${url.split('?').shift()}?${queryString.stringify(params)}`;
};

/**
 * Replace named tokens in a url using values provided
 *
 * @example
 * ```javascript
 * compile('google.com/:foo/:bar', { foo: 'hello', bar: 'world' })
 * // --> 'google.com/hello/world'
 * ```
 */
export const compile = (urlTemplate: string, values: IOptionalStringMap): string =>
  Object.entries(values).reduce((acc, [key, value]) => {
    return acc.replace(`:${key}`, (value ?? '').toString());
  }, urlTemplate);

/**
 * Extract named values from a url using known route patterns
 *
 * @example
 * getUrlValues([{path: 'google.com/:foo/:bar', pattern: UrlPattern}], 'google.com/hello/world')
 * // --> { foo: 'hello', bar: 'world' }
 */
export const getUrlValues = (routes: IRouteDefinition[], url = ''): IStringMap => {
  return matchRoute(url, routes || [])?.match ?? {};
};

// Create a pattern object for a known App route
export const getRoutePattern = (route: string): IRouteDefinition => ({
  path: route,
  pattern: new UrlPattern(route)
});

// extract the path from a url
export const getUrlPath = (url = ''): string => urlParse(url).pathname;

// extract the document/page name from a url
export const getUrlFilename = (url = ''): string => urlParse(url).pathname.split('/').pop() ?? '';

//  extract the last nth segment from a useRouter pathName, etc: input: /internet/network-connection-device/hfc, Get last 2nd segment output: /network-connection-device/hfc
export const getLastNthSegments = (url = '', n = 1): string => {
  const { pathname } = urlParse(url);
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < n) {
    logger.warn('n parameter in getLastNthSegments is bigger than pathName segments length');
    return '';
  }

  return `/${segments.slice(-n).join('/')}`;
};

// convert a full url to a root-relative one by removing protocol and host
export const getRootRelativeUrl = (url: string = ''): string => url.replace(/^.*\/{2}[^/]+/, '');

export const withBasePath = (path?: string): string => {
  const { basePath } = getConfig().publicRuntimeConfig;

  if (!path) {
    return basePath;
  }

  if (path.startsWith(basePath)) {
    return path;
  }

  return `${basePath}/${path}`.replaceAll('//', '/').replace(/\/$/, '');
};

// remove the first segment or specified basePath from an internal url path
export const withoutBasePath = (path?: string, basePath?: string): string => {
  const effectiveBasePath = basePath ?? getConfig().publicRuntimeConfig.basePath;
  const inputPath = path ?? '';

  if (effectiveBasePath) {
    if (!inputPath.startsWith(effectiveBasePath)) {
      return inputPath;
    }

    // strip basePath plus one optional leading slash, then re-add a leading slash
    const rest = inputPath.slice(effectiveBasePath.length);
    return `/${rest.replace(/^\//, '')}`;
  }

  // no basePath: strip the first path segment
  return inputPath.replace(/^\/[^/#?]+\/?/, '/');
};

// removes param values, if present, appended to a path, e.g. /support?anything=1 => /support
export const withoutParams = (path: string): string => {
  return path.split('?').shift() ?? '';
};

// remove hash and query params
export const withoutParamsAndHash = (path: string): string => {
  return path.replace(/[?#].*$/, '');
};

// If within the current app, do a fancy SPA routing, otherwise full page load
export const redirectTo = (route: string): ReturnType<typeof Router.push> | void => {
  const { basePath } = getConfig().publicRuntimeConfig;
  return route.startsWith(basePath) ? Router.push(withoutBasePath(route)) : navigateToExternalPage(route);
};

/**
 * Check if url is a known route.
 * Compares tokenized and non-tokenized urls
 *
 * Notes:
 * This also protects against paths that may start with the app's basePath, but aren't actually handled by the app
 * For example: /support/mobile (new-stack) - /support/mobile/getting-started (classic)
 *
 * Also caters for app paths that do NOT start with the basePath
 * For example: /login
 *
 * If the url contains tokens, it won't match against the route patterns,
 * so we can compile the url to replace the tokens with an 'x' char it can be validated
 */
export const isKnownRoute = (routes: IRouteDefinition[], href = '', asUrl = ''): boolean =>
  routes.some(route => {
    const cleanedHref = withoutParamsAndHash(href);
    const cleanedAsUrl = withoutParamsAndHash(asUrl);

    // check for un-compiled pattern match
    if (route.path === cleanedHref || route.path === cleanedAsUrl) {
      return true;
    }

    // compile given route and then try to match it against known patterns
    const { names } = route.pattern as any;
    const values = names.reduce((acc, name) => ({ ...acc, [name]: 'x' }), {});
    const compiledUrl = compile(cleanedAsUrl || cleanedHref, values);
    return Boolean(route.pattern.match(href) ?? route.pattern.match(compiledUrl));
  });

/**
 * TLRD: find dynamic route pattern from actual example and predefined routes
 *
 * The `matchRoute` function takes a URL `path` and an array of `IRouteDefinition` objects
 * and finds a matching route. If a match is found, it returns an `IRouteMatch` object
 * with properties derived from the matched route. If no match is
 * found, it returns null.
 *
 * The function can handle dynamic path segments like "/new/test/:id" and wildcards like
 * "/new/wild/*". It also ignores search parameters and hash fragments in the `path`.
 *
 * @param path The URL path to match against the routes.
 * @param routes An array of route definitions.
 * @returns A matching route object or null.
 */
export const matchRoute = (path: string, routes: IRouteDefinition[]): IRouteMatch | null => {
  // remove hash and search before comparisons
  const cleanedPath = withoutParamsAndHash(path);
  const route = routes.find(({ pattern }) => !!pattern.match(cleanedPath));
  if (!route) {
    return null;
  }

  const match = route.pattern.match(cleanedPath);
  return {
    pathname: route.path.replaceAll('*', match._),
    spec: route.path,
    match
  };
};

export const redirectInProgress = (currentUrl: string): boolean =>
  new URLSearchParams(currentUrl.split('?').pop()).has('redirect');

export const getSanitisedRedirectUrl = (url?: string): string => {
  return isRelativeUrl(url) ? (url ?? '/') : '/';
};

export const serverRedirect = (res: ServerResponse, url: string, status = 302): void => {
  res.writeHead(status, { Location: url }).end();
};

/**
 * Some query params might be repeated in a URL. That is why sometimes the value
 * of a query param is of type string[] instead of string.
 * This method returns the latest value of that array if there are repeated params.
 * This means we guarantee the type is always `string | undefined`.
 * @param query
 *   The query parameter NextJS router.query
 * @param paramName
 *   The name of the parameter to read value for from the query object.
 */
export const stringValueFromQuery = (
  query: Record<string, string | string[] | undefined>,
  paramName: string
): string | undefined => {
  const param = query[paramName];
  if (Array.isArray(param)) {
    return param[param.length - 1];
  }

  return param;
};

/**
 * Returns the full URL from server side or client side
 * @param ctx
 *   The nextjs context object
 */
export const getFullServerOrClientUrl = (ctx: NextPageContext): string => {
  let url = '';

  if (isBrowser()) {
    url = window.location.href;
  } else if (ctx.req?.url && ctx.req?.headers?.host) {
    const protocol = getConfig().publicRuntimeConfig.env.isDevelopment ? 'http' : 'https';
    const pagePath = protocol === 'http' && ctx.req?.url?.endsWith('.json') ? '/' : withBasePath(ctx.req?.url);
    return `${protocol}://${ctx.req.headers.host}${pagePath}`;
  }

  return url;
};

export const navigateToExternalPage = (url: string): void => window.location.assign(url);

export const isPaymentUrl = (url: string): boolean => paymentUrls.includes(url);

/**
 * given a pathname with square notation eg. /new/services/[serviceId]/payment
 * this function replaces anything inside the square notation with a named path segment
 * /new/services/:serviceId/payment
 * @param url
 * @returns string
 */
export const transformSquareNotationToNamedSegmentPath = (url: string): string => url.replace(/\[(.*?)\]/g, ':$1');

export const urls = {
  APP_STORE_IOS: 'https://apps.apple.com/au/app/belong/id929905619',
  APP_STORE_ANDROID: 'https://play.google.com/store/apps/details?id=com.belong',
  LOGIN: '/new/login',
  MY_PROFILE: '/new/profile',
  MY_SERVICES: '/new/services',
  SECOND_LIFE_DEVICES: 'https://shop.belong.com.au',
  SUPPORT: '/support',
  BLOG: '/go/blog',
  HOME: '/',
  NBN_PLANS: '/go/internet',
  NBN_CART: '/internet/cart',
  MOBILE_DATA_PLANS: '/go/data-plans',
  MOBILE_PLANS: '/go/mobile',
  INTERNATIONAL_ROAMING: '/go/international-roaming',
  OUR_MOBILE_NETWORK: '/go/our-mobile-network',
  COVERAGE_MAP: '/go/mobile/coverage-map',
  NBN_SERVICE_QUALIFICATION: '/go/internet/check-your-address',
  AGENT_AUTHENTICATION: '/nbn-agent/partner',
  SUSTAINABILITY: '/go/sustainability',
  SEARCH: '/go/search'
};

export const paymentUrls = [
  '/new/activate/data/:orderId/add-payment-method',
  '/new/activate/mobile/:orderId/add-payment-method',
  '/payments/:token',
  '/new/services/:serviceId/update-payment',
  '/services/broadband/:serviceId/update-payment',
  '/internet/payment-method',
  '/services/broadband/:serviceId/move/payment'
];

/*
 * Replace named segments, example `:serviceId`, in url with values from current browser address
 * if the named segment of the url passed into this function and that of the browser match.
 * Otherwise return the url as is
 */
export const useDecoratedUrl = (url: string): string => {
  const router = useRouter();

  const { query } = urlParse(url, true);

  if (!url) {
    return '';
  }

  const route = new UrlPattern(url);
  const asPath = router?.asPath;
  const namedRoute = matchRoute(asPath, Object.values(MYACCOUNT_INTERNAL_ROUTES).map(getRoutePattern));

  return route.stringify({
    serviceId: query.serviceId ?? namedRoute?.match.serviceId ?? ':serviceId',
    orderId: query.orderId ?? namedRoute?.match.orderId ?? ':orderId'
  });
};

/*
 * Remove trailing slash from url that ends with one
 */
export const removeTrailingSlash = (url?: string): string | undefined => {
  return url?.endsWith('/') ? url?.slice(0, -1) : url;
};
